/**
 * «Данные → Аудио → Диалоги» — ручная разметка разговоров (владелец 14.09, раздел L; разбор 15.09 — T в
 * services/plugins/speech_to_text/TODO.md).
 *
 * Зачем: разборы владельца приходят текстом («1:07.0 жужжит чековая машинка», «0:04.4 «М?» кассира»),
 * и по ним нельзя ни померить конвейер, ни потюнить его. Здесь то же размечается на волне.
 *
 * Список — диалоги дня так же, как их видят «Сегменты»: прод-разговор, пока его не трогали, и его копия
 * (или новая рамка), если границы поправили. Фильтр выбирает, какие показать. Звук каждого диалога
 * собирается из клипов тем же правилом, что прод (dayStream.glueClips), — дыры и наложения склейки
 * видны на волне всегда (T17).
 */
import { Select } from '@/components/ui/select';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ChevronDown, ChevronRight, Flag, Loader2 } from 'lucide-react';
import {
  getConversations, getConversationMarkup, saveConversationMarkup, flagConversation, getConversationAudioBlob,
  getDialogueBoundaries, getBoundaryMarkup, saveBoundaryMarkup,
  type AudioRecordRaw, type Conversation, type DialogueBoundary, type TranscriptLine, type DialogueLine,
} from '@/api/conversations';
import { getAudioPoolZones } from '@/api/audioPool';
import { Button } from '@/components/ui/button';
import { glueClips, loadDay, loadDayConversations, mergeDialogues, t0, t1, type DayDialogue, type GlueReport } from './dayStream';
import MarkupEditor, { type EditorSource, type TSpan, type DSpan } from './MarkupEditor';

const todayMsk = () => new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
const mskTime = (ms: number) =>
  new Date(ms).toLocaleTimeString('ru-RU', { timeZone: 'Europe/Moscow', hour: '2-digit', minute: '2-digit', second: '2-digit' });

type Filter = 'all' | 'prod' | 'changed' | 'marked' | 'flagged';
const FILTERS: { v: Filter; label: string }[] = [
  { v: 'all', label: 'все диалоги' },
  { v: 'prod', label: 'только неизменные с прода' },
  { v: 'changed', label: 'только изменённые в «Сегментах»' },
  { v: 'marked', label: 'размечено человеком' },
  { v: 'flagged', label: 'помеченные для разметки (все дни)' },
];

/** Строки прода одного разговора на шкале файла: shift — где ноль ЕГО склейки в этом файле. */
function prodSpans(c: Conversation, shift: number): { transcript: TSpan[]; dialogue: DSpan[] } {
  const raw = c.quality_score?.raw_llm_response;
  const offset = shift + (raw?.player_offset_sec ?? 0);
  const tl: TranscriptLine[] = raw?.transcript_lines ?? [];
  const dl: DialogueLine[] = raw?.dialogue_lines ?? [];
  return {
    transcript: tl.filter((l) => (l.text || '').trim() || l.gap || l.tone)
      .map((l) => ({ start: (l.t ?? 0) + offset, end: (l.t ?? 0) + offset + Math.max(0.05, l.dur ?? 0.3), l, conv: c.id })),
    dialogue: dl.map((l, i) => ({
      start: l.t + offset,
      end: l.t + offset + Math.max(0.2, l.dur ?? (l.speaker === 'pause' ? Math.max(0.3, (dl[i + 1]?.t ?? l.t + 1) - l.t) : 0.8)),
      l, conv: c.id,
    })),
  };
}

/** Где в склеенном файле дыры и наложения: по recorded_at и длительностям клипов, от начала первого. */
function clipLayout(mine: AudioRecordRaw[]): Omit<GlueReport, 'blob'> {
  const xs = [...mine].sort((p, q) => t0(p) - t0(q));
  const holes: { at: number; dur: number }[] = []; const overlaps: { at: number; dur: number }[] = [];
  if (!xs.length) return { clips: 0, failed: [], holes, overlaps };
  const base = t0(xs[0]); let end = t1(xs[0]);
  for (const c of xs.slice(1)) {
    const gap = (t0(c) - end) / 1000;
    if (gap > 0.05) holes.push({ at: (end - base) / 1000, dur: gap });
    else if (gap < -0.05) overlaps.push({ at: (t0(c) - base) / 1000, dur: Math.min(-gap, (t1(c) - t0(c)) / 1000) });
    end = Math.max(end, t1(c));
  }
  return { clips: xs.length, failed: [], holes, overlaps };
}

type ItemWithMarkup = DialogueBoundary & { events?: unknown[] };

export default function ConversationMarkupTool() {
  const { data: zonesResp, error: zonesErr, mutate: retryZones } = useSWR('audio-pool-zones', () => getAudioPoolZones().then((r) => r.data));
  const zones = zonesResp?.zones ?? [];
  // Филиал и фильтр — из адреса (Алексей 18.09, AI73): ссылка на очередь
  // разметки должна открывать очередь, а не первый попавшийся филиал за сегодня.
  const [searchParams, setSearchParams] = useSearchParams();
  const zoneParam = Number(searchParams.get('zone')) || null;
  const filterParam = searchParams.get('filter') as Filter | null;
  const [zoneId, setZoneId] = useState<number | null>(zoneParam);
  const [day, setDay] = useState(searchParams.get('day') || todayMsk());
  const [filter, setFilter] = useState<Filter>(
    filterParam && FILTERS.some((f) => f.v === filterParam) ? filterParam : 'all');
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => { if (zoneId == null && zones.length) setZoneId(zones[0].zone_id); }, [zones, zoneId]);
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (zoneId != null) next.set('zone', String(zoneId));
      next.set('filter', filter);
      return next;
    }, { replace: true });
  }, [zoneId, filter, setSearchParams]);

  const flaggedMode = filter === 'flagged';
  const { data: clips, isLoading: clipsLoading, error: clipsErr } = useSWR(zoneId == null || flaggedMode ? null : ['markup-day', zoneId, day],
    () => loadDay(zoneId as number, day), { revalidateOnFocus: false });
  const { data: bounds, mutate: mutateBounds } = useSWR(zoneId == null || flaggedMode ? null : ['seg-bounds', zoneId, day],
    () => getDialogueBoundaries(zoneId as number, day), { revalidateOnFocus: false });
  const { data: convs, mutate: mutateConvs } = useSWR(zoneId == null || flaggedMode ? null : ['markup-day-convs', zoneId, day],
    () => loadDayConversations(zoneId as number, day), { revalidateOnFocus: false });
  const { data: flagged, mutate: mutateFlagged } = useSWR(zoneId == null || !flaggedMode ? null : ['markup-flagged', zoneId],
    () => getConversations(zoneId as number, undefined, undefined, 1, { withPartial: true, flagged: true }).then((r) => r.results),
    { revalidateOnFocus: false });

  const convById = useMemo(() => new Map((flaggedMode ? flagged : convs)?.map((c) => [c.id, c]) ?? []), [convs, flagged, flaggedMode]);
  const dialogues = useMemo<DayDialogue[]>(() => {
    if (flaggedMode) {
      return (flagged ?? []).map((c) => ({ key: `conv-${c.id}`, origin: 'prod' as const, a: Date.parse(c.started_at), b: Date.parse(c.ended_at ?? c.started_at), convId: c.id, item: null }));
    }
    if (!clips || !bounds) return [];
    return mergeDialogues(clips, bounds.items);
  }, [clips, bounds, flagged, flaggedMode]);
  const marked = (d: DayDialogue) => (d.item ? ((d.item as ItemWithMarkup).events?.length ?? 0) > 0 : (convById.get(d.convId as number)?.markup?.events ?? 0) > 0);
  const shown = dialogues.filter((d) => filter === 'all' || filter === 'flagged' || (filter === 'prod' ? d.origin === 'prod'
    : filter === 'changed' ? d.origin !== 'prod' : marked(d)));
  const loading = (!flaggedMode && (clipsLoading || !bounds || !convs)) || (flaggedMode && !flagged);
  const refresh = () => { void mutateBounds(); void mutateConvs(); void mutateFlagged(); };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <Select className="h-8" wrapperClassName="w-auto" value={zoneId ?? ''}
          onChange={(e) => { setZoneId(Number(e.target.value)); setOpen(null); }}>
          {zones.map((z) => <option key={z.zone_id} value={z.zone_id}>{z.zone || `зона ${z.zone_id}`}</option>)}
        </Select>
        <input type="date" className="rounded-md border bg-background px-2 py-1 disabled:opacity-50" value={day}
          disabled={flaggedMode} onChange={(e) => { setDay(e.target.value); setOpen(null); }} />
        <Select className="h-8" wrapperClassName="w-auto" value={filter} onChange={(e) => { setFilter(e.target.value as Filter); setOpen(null); }}>
          {FILTERS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
        </Select>
        {loading && !zonesErr && !clipsErr && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
        <span className="ml-auto text-muted-foreground">
          {shown.length} {filter === 'all' ? `диалогов (изменено ${dialogues.filter((d) => d.origin !== 'prod').length})` : `из ${dialogues.length}`}
        </span>
      </div>
      {(zonesErr || clipsErr) && (
        <div className="flex items-center gap-2 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
          {zonesErr ? 'Список зон не загрузился.' : 'Поток клипов не загрузился.'}
          <Button size="sm" variant="outline" onClick={() => void retryZones()}>повторить</Button>
        </div>
      )}
      <div className="divide-y rounded-lg border bg-card">
        {shown.map((d) => {
          const c = d.convId != null ? convById.get(d.convId) : undefined;
          const n = d.item ? ((d.item as ItemWithMarkup).events?.length ?? 0) : (c?.markup?.events ?? 0);
          return (
            <div key={d.key}>
              <button type="button" onClick={() => setOpen(open === d.key ? null : d.key)}
                className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-muted/40">
                {open === d.key ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                <span className="inline-block size-3 shrink-0 rounded-sm border" style={{ background: d.item?.color || 'transparent' }} />
                <span className="font-mono tabular-nums">{flaggedMode ? new Date(d.a).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : `${mskTime(d.a)}–${mskTime(d.b)}`}</span>
                <span className="text-muted-foreground">{((d.b - d.a) / 60000).toFixed(1)} мин</span>
                <span className="text-xs text-muted-foreground">
                  {d.origin === 'prod' ? `прод #${d.convId}` : d.origin === 'changed' ? `изменён #${d.convId}` : 'новый'}
                </span>
                {c?.markup?.flagged && <Flag className="size-3.5 fill-amber-500 text-amber-500" />}
                {n > 0 && <span className="rounded bg-primary/10 px-1.5 text-xs text-primary">размечено {n}</span>}
                <span className="min-w-0 flex-1 truncate text-muted-foreground">{d.item?.comment || c?.quality_score?.summary}</span>
              </button>
              {open === d.key && zoneId != null && (
                <DialogueEditor dialogue={d} conv={c} zoneId={zoneId} day={flaggedMode ? new Date(d.a + 3 * 3600 * 1000).toISOString().slice(0, 10) : day}
                  clips={clips} convById={convById} onChanged={refresh} />
              )}
            </div>
          );
        })}
        {!loading && !shown.length && <div className="px-3 py-6 text-sm text-muted-foreground">Диалогов в этом фильтре нет</div>}
      </div>
    </div>
  );
}

/** Источник для редактора: звук из клипов, строки прода под диалогом, где хранить разметку. */
function DialogueEditor({ dialogue: d, conv, zoneId, day, clips: dayClips, convById, onChanged }: {
  dialogue: DayDialogue; conv?: Conversation; zoneId: number; day: string; clips?: AudioRecordRaw[];
  convById: Map<number, Conversation>; onChanged: () => void;
}) {
  // «Помеченные за все дни» открываются без загруженного дня — поток клипов нужного дня грузим здесь.
  const { data: ownClips } = useSWR(dayClips ? null : ['markup-day', zoneId, day], () => loadDay(zoneId, day), { revalidateOnFocus: false });
  const clips = dayClips ?? ownClips;
  const a = d.a;
  const source = useMemo<EditorSource | null>(() => {
    if (!clips) return null;
    if (!d.item && conv) {
      // Прод-разговор как есть — ТЕМ ЖЕ файлом и теми же строками, что плеер разговоров на
      // проде: склеенный файл разговора (audio_url_full) и время строк + player_offset_sec.
      // Раньше здесь клипы клеились заново в браузере, и запись расходилась с продом: у
      // разговора 15.09 19:12 в начале стоял лишний кусок (владелец 15.09). Разметка,
      // снятая с другой склейки, поехала бы относительно прода — а учимся мы на проде.
      // Сборка из клипов — только для старых разговоров, у которых склеенного файла нет.
      const raw = conv.quality_score?.raw_llm_response;
      const mine = clips.filter((c) => c.conversation === conv.id && c.recorded_at);
      const spans = prodSpans(conv, 0);
      return {
        key: `conv-${conv.id}`,
        audio: async () => {
          if (conv.audio_url_full) {
            // Дыры и наложения — по тем же клипам и тому же времени записи, по которым прод
            // клеил файл (glue.build_conversation_audio): дыру он заполняет нулями, и без
            // отметки она выглядит «абсолютной тишиной» (владелец 15.09, 20792).
            return { blob: await getConversationAudioBlob(conv.id), note: 'файл разговора с прода · ', glue: clipLayout(mine) };
          }
          if (!mine.length) throw new Error('У разговора нет ни склеенного файла, ни клипов со временем записи.');
          const s = Math.min(...mine.map(t0));
          const e = raw?.player_end_sec ? s + raw.player_end_sec * 1000 : Math.max(...mine.map(t1));
          const { blob, ...glue } = await glueClips(mine, s, e);
          return { blob, glue };
        },
        transcript: spans.transcript,
        dialogue: spans.dialogue,
        load: () => getConversationMarkup(conv.id),
        save: async (body) => saveConversationMarkup(conv.id, body),
        flag: async (v) => (await flagConversation(conv.id, v)).flagged,
      };
    }
    if (!d.item) return null;
    const item = d.item;
    const b = d.b;
    const under = [...new Set(clips.filter((c) => c.recorded_at && t1(c) > a && t0(c) < b && c.conversation != null).map((c) => c.conversation as number))];
    const transcript: TSpan[] = []; const dialogue: DSpan[] = [];
    for (const id of under) {
      const c = convById.get(id);
      const mine = clips.filter((x) => x.conversation === id && x.recorded_at);
      if (!c || !mine.length) continue;
      const s = prodSpans(c, (Math.min(...mine.map(t0)) - a) / 1000);
      transcript.push(...s.transcript); dialogue.push(...s.dialogue);
    }
    return {
      key: `frame-${item.id}-${item.start}-${item.end}`,
      audio: async () => {
        if (b - a > 20 * 60 * 1000) throw new Error('Диалог длиннее 20 минут — собирать такой файл в браузере не стоит.');
        const { blob, ...glue } = await glueClips(clips, a, b);
        if (!glue.clips) throw new Error('В окне диалога нет клипов со звуком.');
        return { blob, glue };
      },
      transcript, dialogue,
      load: async () => {
        try { return await getBoundaryMarkup(zoneId, day, item.id); }
        catch { return { events: [], replies: [], comment: '', updated_by: null, flagged: false }; }
      },
      save: (body) => saveBoundaryMarkup(zoneId, day, item.id, body),
      // Копия прод-разговора: если прод-разговор уже размечали, переносим его разметку со сдвигом.
      fallback: item.conversation_id == null ? undefined : async () => {
        const m = await getConversationMarkup(item.conversation_id as number);
        const mine = clips.filter((x) => x.conversation === item.conversation_id && x.recorded_at);
        if (!m.events.length || !mine.length) return null;
        const shift = (Math.min(...mine.map(t0)) - a) / 1000;
        const move = <T extends { start: number; end: number }>(x: T): T => ({ ...x, start: +(x.start + shift).toFixed(3), end: +(x.end + shift).toFixed(3) });
        return { ...m, events: m.events.map(move), dialogue: (m.dialogue ?? []).map(move) };
      },
    };
  }, [clips, conv, d, a, zoneId, day, convById]);
  if (clips && !source) return <div className="px-3 py-2 text-sm text-muted-foreground">Разговор не нашёлся в списке этого дня — обнови страницу.</div>;
  if (!source) return <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> поток клипов грузится</div>;
  return <MarkupEditor source={source} onChanged={onChanged} frameStartMs={d.item ? a : undefined} />;
}
